import React from 'react';
import { Check, Image, Trash, Upload } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import AIPremiumButton from './AIPremiumGate';
import { DEFAULT_BACKGROUND_OPTIONS } from '../config/defaultThemeOptions';
import { resolveMediaUrl } from '../utils/media';
import InfoTip from './InfoTip';

export default function BackgroundImageOptions({
  selectedBackground,
  uploadedBackground,
  currentTheme,
  onSelectBackground,
  generatedBackgrounds,
  backgroundInputRef,
  onUploadBackground,
  onRemoveUploadedBackground,
  viewAudienceLabel = 'guest and host views',
}) {
  const activeBackground = selectedBackground
    || currentTheme?.selected_background_url
    || currentTheme?.custom_image_url
    || currentTheme?.generated_image_url;

  return (
    <Card data-testid="background-image-options">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Image className="w-5 h-5" />
          Background Image
          <InfoTip content={`Add an optional decorative image behind ${viewAudienceLabel}.`} label="About background images" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="flex items-center gap-1">
              <h3 className="font-medium">Included backgrounds</h3>
              <InfoTip content="Pick a design, generate one, or upload your own." label="About included backgrounds" />
            </div>
            <div className="flex flex-wrap gap-2">
              <AIPremiumButton
                featureName="AI theme background generation"
                variant="outline"
                data-testid="generate-bg-image-btn"
              >
                Generate Background
              </AIPremiumButton>
              <Button
                variant="outline"
                onClick={() => backgroundInputRef.current?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Image
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
            {DEFAULT_BACKGROUND_OPTIONS.map((background) => {
              const isSelected = activeBackground === background.url;
              return (
                <button
                  key={background.id}
                  type="button"
                  onClick={() => onSelectBackground(background.url)}
                  className={`relative overflow-hidden rounded-lg border bg-white text-left transition-all ${
                    isSelected ? 'ring-2 ring-offset-2 border-transparent' : 'hover:shadow-md'
                  }`}
                  style={{ '--tw-ring-color': 'var(--theme-primary, #5D7061)' }}
                  data-testid={`default-background-${background.id}`}
                >
                  <img
                    src={background.url}
                    alt={background.name}
                    className="w-full h-24 sm:h-40 object-cover object-center"
                  />
                  <span className="block px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium">
                    {background.name}
                  </span>
                  {isSelected && (
                    <div className="absolute inset-x-0 top-0 h-24 sm:h-40 bg-black/20 flex items-center justify-center">
                      <Check className="w-6 h-6 sm:w-7 sm:h-7 text-white drop-shadow" weight="bold" />
                    </div>
                  )}
                </button>
              );
            })}
            {uploadedBackground && (
              <div
                className={`relative overflow-hidden rounded-lg border bg-white text-left transition-all ${
                  activeBackground === uploadedBackground
                    ? 'border-transparent ring-2 ring-offset-2'
                    : 'hover:shadow-md'
                }`}
                style={{ '--tw-ring-color': 'var(--theme-primary, #5D7061)' }}
                data-testid="uploaded-background-tile"
              >
                <button
                  type="button"
                  onClick={() => onSelectBackground(uploadedBackground)}
                  className="block w-full text-left"
                  aria-pressed={activeBackground === uploadedBackground}
                  aria-label="Select uploaded background image"
                >
                  <img
                    src={resolveMediaUrl(uploadedBackground)}
                    alt="Uploaded background"
                    className="h-24 w-full object-cover object-center sm:h-40"
                  />
                  <span className="block px-2 py-1.5 text-xs font-medium sm:px-3 sm:py-2 sm:text-sm">
                    Uploaded image
                  </span>
                  {activeBackground === uploadedBackground && (
                    <div className="pointer-events-none absolute inset-x-0 top-0 flex h-24 items-center justify-center bg-black/20 sm:h-40">
                      <Check className="h-6 w-6 text-white drop-shadow sm:h-7 sm:w-7" weight="bold" />
                    </div>
                  )}
                </button>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  onClick={onRemoveUploadedBackground}
                  className="absolute right-1.5 top-1.5 z-10 h-8 w-8 rounded-full bg-white/90 text-destructive shadow hover:bg-white"
                  aria-label="Remove uploaded background image"
                  data-testid="remove-uploaded-background-btn"
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {generatedBackgrounds.length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-muted-foreground mb-2">Generated Options (Select One)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                {generatedBackgrounds.map((url, index) => {
                  const isSelected = activeBackground === url;
                  return (
                    <button
                      key={`${url}-${index}`}
                      type="button"
                      onClick={() => onSelectBackground(url)}
                      className={`relative rounded-lg overflow-hidden border transition-all ${
                        isSelected ? 'ring-2 ring-offset-2 border-transparent' : 'hover:shadow-md'
                      }`}
                    >
                      <img
                        src={resolveMediaUrl(url)}
                        alt={`Generated background ${index + 1}`}
                        className="w-full h-20 sm:h-24 object-cover"
                      />
                      {isSelected && (
                        <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
                          <Check className="w-6 h-6 text-white" weight="bold" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <input
            ref={backgroundInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onUploadBackground}
          />
        </div>
      </CardContent>
    </Card>
  );
}
